import { Injectable } from '@angular/core';
import { Owned } from 'app/model/base/named';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { Flowlet } from 'app/model/dataflow/flowlet';
import { ModelService } from 'app/model/services/model.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface IDataFlowTabOpenRequest {
    dataFlow: DataFlow;
    resultSubject: BehaviorSubject<boolean>;
}

@Injectable({
    providedIn: 'root'
})
export class DataFlowCleanupService {
    public _dataFlowsToCleanUp: Set<DataFlow>;
    public readonly isTabOpen$: Observable<IDataFlowTabOpenRequest>;
    private readonly _isTabOpen$: Subject<IDataFlowTabOpenRequest>;

    constructor(private readonly _modelService: ModelService) {
        this._isTabOpen$ = new Subject<IDataFlowTabOpenRequest>();
        this.isTabOpen$ = this._isTabOpen$;
        this._dataFlowsToCleanUp = new Set<DataFlow>();
    }

    public isDataFlowTabOpen(dataFlow: DataFlow): boolean {
        const resultSubject = new BehaviorSubject<boolean>(false);
        // Ask tab manager if data flow is in an open tab
        this._isTabOpen$.next({ dataFlow, resultSubject });
        return resultSubject.value;
    }

    public cleanUpDataFlow(owned: Owned, entitiesWithViolations?: Set<Owned>): void {
        if (owned instanceof DataFlow && !this.excludeFromCleanup(owned) &&
                !(entitiesWithViolations && entitiesWithViolations.has(owned)) && !this.isDataFlowTabOpen(owned)) {
            owned.tearDown(this._modelService.modelAttributeContext);
        }
        return;
    }

    public addDataFlowToCleanUp(owned: Owned): void {
        if (owned instanceof DataFlow) {
            this._dataFlowsToCleanUp.add(owned);
        }
    }

    public cleanUpStoredDataFlows(): void {
        this._dataFlowsToCleanUp.forEach((dataFlow) => this.cleanUpDataFlow(dataFlow));
        this._dataFlowsToCleanUp.clear();
    }

    public excludeFromCleanup(dataFlow: DataFlow): boolean {
        // For some reason tearing down data flows with fault tolerance set clears the value. Investigating more, for now this resolves it.
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return dataFlow.sinks && !!dataFlow.sinks.find((sink) => !!sink.faultToleranceLinkedService)
            || dataFlow instanceof Flowlet
            || dataFlow.transformations && !!dataFlow.transformations.find((transformation) => !!(transformation as any).flowlet);
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }
}
